import { CheckOutlined } from "@ant-design/icons";
import { theme, Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import { KEY_ARROW_DOWN, KEY_ARROW_LEFT, KEY_ARROW_RIGHT, KEY_ARROW_UP, KEY_ENTER, KEY_SPACE } from "../../../../../shared/frontend/ui/keyboardKeys";
import { borderRadius, spacing, typography } from "../app/design-system";

export type CheckmarkChoiceCardProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  selectionRole?: "button" | "radio";
  tabIndex?: number;
  onSelect: () => void;
};

/** Sichtbare Auswahlkarte mit Checkmark; der Selector ergänzt die Einzelauswahl-Semantik. */
export function CheckmarkChoiceCard({
  title,
  description,
  selected = false,
  disabled = false,
  selectionRole = "button",
  tabIndex = 0,
  onSelect,
}: CheckmarkChoiceCardProps) {
  const { token } = theme.useToken();

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === KEY_ENTER || event.key === KEY_SPACE) {
        event.preventDefault();
        onSelect();
      }
    },
    [disabled, onSelect]
  );

  return (
    <div
      role={selectionRole}
      tabIndex={disabled ? -1 : tabIndex}
      aria-checked={selectionRole === "radio" ? selected : undefined}
      aria-pressed={selectionRole === "button" ? selected : undefined}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={handleKeyDown}
      style={{
        border: `1px solid ${selected ? token.colorPrimary : token.colorBorderSecondary}`,
        // Kit-Tokens: `colorPrimaryBg` ist im dunklen Noir ein heller Grauton, auf dem der
        // Sekundärtext der Beschreibung absäuft.
        background: selected ? "var(--mq-theme-mark-soft)" : token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        padding: spacing.md,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        display: "flex",
        alignItems: description ? "flex-start" : "center",
        gap: spacing.sm,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 22,
          height: 22,
          borderRadius: borderRadius.legacyPill,
          border: `1px solid ${selected ? token.colorPrimary : token.colorBorder}`,
          background: selected ? token.colorPrimary : token.colorBgContainer,
          // Haken in der Gegenfarbe der Primärfläche; `colorWhite` verschwindet auf hellem Primär (Noir dunkel).
          color: "var(--mq-theme-on-primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: description ? spacing.hairline : 0,
        }}
      >
        {selected ? <CheckOutlined style={{ fontSize: typography.fontSize.sm }} /> : null}
      </div>
      <div style={{ minWidth: 0 }}>
        <Typography.Text strong>{title}</Typography.Text>
        {description ? (
          <Typography.Text
            type="secondary"
            style={{ display: "block", fontSize: typography.fontSize.sm, marginTop: spacing.xs }}
          >
            {description}
          </Typography.Text>
        ) : null}
      </div>
    </div>
  );
}

export function CheckmarkChoiceSelector<V extends string | number | boolean>({
  value,
  onChange,
  options,
}: {
  value?: V;
  onChange?: (value: V) => void;
  options: Array<{ value: V; title: React.ReactNode; description?: React.ReactNode; disabled?: boolean }>;
}) {
  const selectedIndex = options.findIndex((option) => option.value === value && !option.disabled);
  const fallbackIndex = options.findIndex((option) => !option.disabled);
  const tabStopIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;

  const moveSelection = (event: React.KeyboardEvent<HTMLDivElement>, delta: number) => {
    if (options.length === 0 || fallbackIndex < 0) return;
    event.preventDefault();
    let nextIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;
    do {
      nextIndex = (nextIndex + delta + options.length) % options.length;
    } while (options[nextIndex]?.disabled);
    onChange?.(options[nextIndex]!.value);
    const cards = event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]');
    cards[nextIndex]?.focus();
  };

  return (
    <div
      role="radiogroup"
      onKeyDown={(event) => {
        if (event.key === KEY_ARROW_RIGHT || event.key === KEY_ARROW_DOWN) moveSelection(event, 1);
        else if (event.key === KEY_ARROW_LEFT || event.key === KEY_ARROW_UP) moveSelection(event, -1);
      }}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: spacing.sm,
      }}
    >
      {options.map((option, index) => (
        <CheckmarkChoiceCard
          key={String(option.value)}
          title={option.title}
          description={option.description}
          selected={option.value === value}
          disabled={option.disabled}
          selectionRole="radio"
          tabIndex={index === tabStopIndex ? 0 : -1}
          onSelect={() => onChange?.(option.value)}
        />
      ))}
    </div>
  );
}
